import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type CriticalIllnessNYProps = {
  planType: StringUnion;
  benefitAmount: number;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  insuredSC: StringUnion;
  spouseSC: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  situsState: StringUnion;
  wellnessUnits: StringUnion;
  ciOption: boolean;
  secondEventCI: boolean;
  jStamp: boolean;
  packageSource: StringUnion;
  packageSourceList: PackageModel;
};

export class InvalidOrdinaryCriticalIllnessNY {
  public readonly key = 'InvalidOrdinaryCriticalIllnessNY';

  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly wellnessUnits: StringUnion,
    public readonly ciOption: boolean,
    public readonly secondEventCI: boolean,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel
  ) {}
}

export class ValidOrdinaryCriticalIllnessNY {
  public static createApp({
    planType,
    benefitAmount,
    premium,
    calculatedPremium,
    cwa,
    insuredSC,
    spouseSC,
    section125,
    replacement,
    policyNumber,
    situsState,
    wellnessUnits,
    ciOption,
    secondEventCI,
    jStamp,
    packageSource,
    packageSourceList,
  }: CriticalIllnessNYProps) {
    return this.create(
      planType,
      benefitAmount,
      premium,
      calculatedPremium,
      cwa,
      insuredSC,
      spouseSC,
      section125,
      replacement,
      policyNumber,
      situsState,
      wellnessUnits,
      ciOption,
      secondEventCI,
      jStamp,
      packageSource,
      packageSourceList
    );
  }

  public static create(
    planType: StringUnion,
    benefitAmount: number,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    insuredSC: StringUnion,
    spouseSC: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    situsState: StringUnion,
    wellnessUnits: StringUnion,
    ciOption: boolean,
    secondEventCI: boolean,
    jStamp: boolean,
    packageSource: StringUnion,
    packageSourceList: PackageModel
  ) {
    return planType &&
      benefitAmount &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      insuredSC &&
      section125 &&
      replacement &&
      policyNumber
      ? new ValidOrdinaryCriticalIllnessNY(
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          wellnessUnits,
          ciOption,
          secondEventCI,
          jStamp,
          packageSource,
          packageSourceList
        )
      : new InvalidOrdinaryCriticalIllnessNY(
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          wellnessUnits,
          ciOption,
          secondEventCI,
          jStamp,
          packageSource,
          packageSourceList
        );
  }

  public readonly key = 'ValidOrdinaryCriticalIllnessNY';
  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly wellnessUnits: StringUnion,
    public readonly ciOption: boolean,
    public readonly secondEventCI: boolean,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel
  ) {}
}

export type OrdinaryCriticalIllnessNY = InvalidOrdinaryCriticalIllnessNY | ValidOrdinaryCriticalIllnessNY;

export type OrdinaryCriticalIllnessNYOptions<TReturn> = ToPartialMatchOptions<
  OrdinaryCriticalIllnessNY,
  'key',
  TReturn
>;

export const matchOrdinaryCriticalIllnessNY = <TReturn>(options: OrdinaryCriticalIllnessNYOptions<TReturn>) => (
  app: OrdinaryCriticalIllnessNY
): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryCriticalIllnessNY':
      return options.InvalidOrdinaryCriticalIllnessNY
        ? options.InvalidOrdinaryCriticalIllnessNY(app)
        : options.default(app);
    case 'ValidOrdinaryCriticalIllnessNY':
      return options.ValidOrdinaryCriticalIllnessNY
        ? options.ValidOrdinaryCriticalIllnessNY(app)
        : options.default(app);
  }
};

export const EmptyOrdinaryCriticalIllnessNY = new InvalidOrdinaryCriticalIllnessNY(
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  new EmptyString(),
  new UnassignedPackageModel()
);

export const changePlanType = (planType: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, planType: NonEmptyString.create(planType)});

export const changeBenefitAmount = (benefitAmount: number) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, benefitAmount});

export const changePremium = (premium: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, premium: NonEmptyString.create(premium)});

  export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeInsuredSC = (insuredSC: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, insuredSC: NonEmptyString.create(insuredSC)});

export const changeSpouseSC = (spouseSC: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, spouseSC: NonEmptyString.create(spouseSC)});

export const changeSection125 = (section125: boolean) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({
    ...app,
    replacement,
    policyNumber: ValidPolicyNumber.create(new EmptyString()),
  });

export const changePolicyNumber = (policyNumber: StringUnion) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeSitusState = (situsState: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changeWellnessUnit = (wellnessUnits: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, wellnessUnits: NonEmptyString.create(wellnessUnits)});

export const changeCIOption = (ciOption: boolean) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, ciOption});

export const changeSecondEventCI = (secondEventCI: boolean) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, secondEventCI});

export const changeJStamp = (jStamp: boolean) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, jStamp});

export const changePackageSource = (packageSource: string) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, packageSource: NonEmptyString.create(packageSource)});

export const changePackageSourceList = (packageSourceList: PackageModel) => (app: OrdinaryCriticalIllnessNY) =>
  ValidOrdinaryCriticalIllnessNY.createApp({...app, packageSourceList});
